<template>
    <div class="rounded-sm overflow-hidden bg-gray-50">
        <div class="bg-gray-100 flex flex-row justify-between items-center gap-x-3 p-3">
            <Button
                size="Small"
                variant="Neutral-Strong"
                :disabled-explanation="newVariantDisabledExplanation"
                :aria-pressed="createAllocationVariantStatus === 'Visible'"
                @click="createAllocationVariantStatus = 'Visible'"
            >
                <template #icon>
                    <PlusIcon class="size-4.5" />
                </template>

                {{ $t('new-allocation-variant-button') }}
            </Button>

            <Button
                class="text-gray-700"
                size="Small"
                variant="Transparent-Light"
                :icon-description="$t('close-allocation-variant-selection-panel')"
                @click="emit('close')"
            >
                <template #icon>
                    <CloseIcon class="size-4.5" />
                </template>
            </Button>
        </div>

        <NewAllocationVariant
            v-if="createAllocationVariantStatus !== 'Hidden'"
            class="mb-2"
            :variant-index="newVariantIndex"
            :status-label="defaultStatusLabel"
            :status-variant="defaultStatusVariant"
            :preselected-variant="null"
            :all-variants="allVariants"
            :create-allocation-variant-status="createAllocationVariantStatus"
            :label-errors="labelErrors"
            @create="createAllocationVariant"
            @abort="createAllocationVariantStatus = 'Hidden'"
        />

        <RadioButtonGroup
            :active-id="activeIdRef"
            :name="nameRef"
            :label="'Hervorgehobene Variante'"
            @change="changeHighlight"
        >
            <template v-for="(entry, entryIndex) in enrichedEntries" :key="entryIndex">
                <RadioButtonEntry
                    :id="entry.type === 'No-Highlight' ? 'No-Highlight-Id' : entry.id"
                    :name="props.name"
                    :input-element-tooltip-text="inputElementTooltipText"
                    :invisible-label="
                        entry.type === 'Existing-Allocation-Variant'
                            ? entry.invisibleRadioButtonLabel
                            : $t('no-highlighted-allocation-variant-label')
                    "
                    :has-highlight="
                        entry.type === 'Existing-Allocation-Variant' &&
                        (!entry.hasAllocations || !entry.hasAllocationsInRange)
                            ? 'Yellow'
                            : undefined
                    "
                >
                    <p v-if="entry.type === 'No-Highlight'" class="text-gray-500 font-light py-3">
                        {{ $t('no-highlighted-allocation-variant') }}
                    </p>

                    <AllocationVariantEntry
                        v-if="entry.type === 'Existing-Allocation-Variant'"
                        :is-visible="entry.isVisible"
                        :is-highlighted="entry.id === activeId"
                        :copy-allocations-button-disabled-explanation="entry.copyAllocationsButtonDisabledExplanation"
                        :status-label="entry.statusLabel"
                        :status-variant="entry.statusVariant"
                        :is-main-variant="entry.isMainVariant"
                        :variant-id="entry.id"
                        :variant-index="entry.index"
                        :variant-name="entry.variantName"
                        :preselected-variant="entry.preselectedVariant"
                        :all-variants="entry.allVariants"
                        :copying-allocations-status="entry.copyAllocationsStatus"
                        :link-target="entry.linkTarget"
                        :has-allocations="entry.hasAllocations"
                        :has-allocations-in-range="entry.hasAllocationsInRange"
                        :category-color="categoryColor"
                        @toggle-visibility="() => toggleVisibility(entry)"
                        @copy-allocations="(copyFrom) => copyAllocations(entry, copyFrom)"
                        @start-copying-allocations="() => startCopyingAllocations(entry.id)"
                        @abort-copying-allocations="() => abortCopyingAllocations(entry.id)"
                    />
                </RadioButtonEntry>

                <Separator v-if="entryIndex < entries.length - 1" />
            </template>
        </RadioButtonGroup>
    </div>
</template>

<script setup lang="ts">
import auth from '@/auth/auth';
import { eventVariantStatusToBadgeVariant, getEventVariantStatusLabel } from '@/event/event';
import { EventVariantStatus } from '@/event/types';
import {
    externalEventVariantStatusToBadgeVariant,
    getExternalEventVariantStatusLabel,
} from '@/external_event/externalEvent';
import { ExternalEventVariantStatus } from '@/generated/graphql';
import { $t } from '@/plugins/fluent';
import { getProjectVariantStatusLabel, projectVariantStatusToBadgeVariant } from '@/project/project';
import { ProjectVariantStatus } from '@/project/types';
import { Ref, computed, ref, toRef, watch, watchEffect } from 'vue';
import { BadgeVariant } from '../Badge/Badge.vue';
import Button from '../Button/Button.vue';
import CloseIcon from '../Icon/CloseIcon.vue';
import PlusIcon from '../Icon/PlusIcon.vue';
import { AllocationType, PhaseColor } from '../Timeline/Timeline.types';
import AllocationVariantEntry from './AllocationVariantEntry.vue';
import NewAllocationVariant from './NewAllocationVariant.vue';
import RadioButtonGroup from './RadioButtonGroup.vue';
import RadioButtonEntry from './RadioButtonGroupEntry.vue';
import Separator from './Separator.vue';
import { ExistingAllocationVariantEntry, NoHighlightEntry, SelectionPanelEntries } from './types';

type AllocationVariantSelectionPanelProps = {
    objectId: string;
    allocationType: AllocationType;
    activeId: string;
    name: string;
    entries: SelectionPanelEntries;
    currentVariant: {
        id: string;
        index: number;
        name: string;
    };
    newVariantIndex: number;
    categoryColor: Extract<
        PhaseColor,
        | 'External-Event-Category-Project'
        | 'External-Event-Category-Event'
        | 'External-Event-Category-External-Event'
        | 'Project'
        | 'Event'
    >;
};

type AllocationVariantSelectionPanelEmits = {
    (e: 'changeHighlight', idHighlightedVariant: string | undefined): void;
    (
        e: 'changeVisibility',
        { allocationVariantId, isVisible }: { allocationVariantId: string; isVisible: boolean },
    ): void;
    (
        e: 'createNewVariant',
        {
            allocationVariantLabel,
            copyAllocationsFrom,
            onDone,
        }: { allocationVariantLabel: string; copyAllocationsFrom: string | null; onDone: (isSuccess: boolean) => void },
    ): void;
    (
        e: 'copyAllocations',
        {
            idTargetVariant,
            idSourceVariant,
            onDone,
        }: { idTargetVariant: string; idSourceVariant: string; onDone: (isSuccess: boolean) => void },
    ): void;
    (e: 'close'): void;
};

const props = defineProps<AllocationVariantSelectionPanelProps>();
const emit = defineEmits<AllocationVariantSelectionPanelEmits>();

const activeIdRef = toRef(props, 'activeId');
const entriesRef = toRef(props, 'entries');
const nameRef = toRef(props, 'name');
const createAllocationVariantStatus = ref<'Hidden' | 'Visible' | 'Loading' | 'Error'>('Hidden');
const labelErrors: Ref<string[]> = ref([]);
const newVariantLabel = ref('');

const newVariantDisabledExplanation = computed(() => {
    if (auth.isAdmin()) {
        if (createAllocationVariantStatus.value !== 'Hidden') {
            return $t('already-creating-new-variant');
        }
    } else {
        return $t('no-permission-to-create');
    }
    return undefined;
});

const badgeVariantFunctions = {
    Event: eventVariantStatusToBadgeVariant,
    Project: projectVariantStatusToBadgeVariant,
    ExternalEvent: externalEventVariantStatusToBadgeVariant,
};

const defaultVariantStatus = computed(() => {
    switch (props.allocationType) {
        case 'Event':
        case 'Project':
            return 'active-1';
        case 'ExternalEvent':
            return ExternalEventVariantStatus.Fix;
    }
});

function getStatusLabel(status: EventVariantStatus | ProjectVariantStatus | ExternalEventVariantStatus) {
    const allocationType = props.allocationType;
    switch (allocationType) {
        case 'Event':
            return getEventVariantStatusLabel(status);
        case 'Project':
            return getProjectVariantStatusLabel(status);
        case 'ExternalEvent':
            return getExternalEventVariantStatusLabel(status);
    }
}

const defaultStatusLabel = computed(() => getStatusLabel(defaultVariantStatus.value));

const defaultStatusVariant = computed(() => {
    return badgeVariantFunctions[props.allocationType](defaultVariantStatus.value);
});

const allVariants = computed(() =>
    props.entries
        .filter((entry): entry is ExistingAllocationVariantEntry => entry.type === 'Existing-Allocation-Variant')
        .map((entry) => {
            return {
                id: entry.id,
                name: formatId(entry.index, entry.variantName),
                hasAllocations: entry.hasAllocations,
                hasAllocationsInRange: entry.hasAllocationsInRange,
            };
        }),
);

type EnrichedEntry = Omit<ExistingAllocationVariantEntry, 'status'> & {
    copyAllocationsButtonDisabledExplanation: string | undefined;
    statusLabel: string;
    statusVariant: BadgeVariant;
    copyAllocationsStatus: 'Hidden' | 'Visible' | 'Loading' | 'Error';
    preselectedVariant: { id: string; name: string } | null;
    allVariants: { id: string; name: string }[];
    invisibleRadioButtonLabel: string;
};

const enrichedEntries = ref<(NoHighlightEntry | EnrichedEntry)[]>([]);

const preselectedVariant = computed(() => ({
    id: props.currentVariant.id,
    name: formatId(props.currentVariant.index, props.currentVariant.name),
}));

function getCopyDisabledExplanation() {
    if (!auth.isAdmin()) return $t('no-permission-to-copy');
    return undefined;
}

watchEffect(() => {
    enrichedEntries.value = props.entries.map((entry) => {
        const badgeFunction = badgeVariantFunctions[props.allocationType];
        if (entry.type === 'Existing-Allocation-Variant') {
            return {
                ...entry,
                copyAllocationsButtonDisabledExplanation: getCopyDisabledExplanation(),
                statusLabel: getStatusLabel(entry.status),
                statusVariant: badgeFunction(entry.status),
                copyAllocationsStatus: 'Hidden',
                preselectedVariant: entry.isMainVariant ? null : preselectedVariant.value,
                allVariants: allVariants.value,
                invisibleRadioButtonLabel: formatId(entry.index, entry.variantName),
            };
        }

        return entry;
    });
});

const inputElementTooltipText = $t('select-to-highlight-variant');

function toggleVisibility(entry: EnrichedEntry) {
    entry.isVisible = !entry.isVisible;
    changeVisibility(entry.id, entry.isVisible);
}

function copyAllocations(entry: EnrichedEntry, sourceId: string) {
    function onDone(isSuccess: boolean) {
        entry.copyAllocationsStatus = isSuccess ? 'Hidden' : 'Error';
    }

    entry.copyAllocationsStatus = 'Loading';

    emit('copyAllocations', {
        idTargetVariant: entry.id,
        idSourceVariant: sourceId,
        onDone,
    });
}

function startCopyingAllocations(id: string) {
    enrichedEntries.value = enrichedEntries.value.map((entry) => {
        if (entry.type === 'Existing-Allocation-Variant' && entry.id === id) {
            return {
                ...entry,
                copyAllocationsStatus: 'Visible',
                copyAllocationsButtonDisabledExplanation: getCopyDisabledExplanation(),
            };
        }
        const explanation = $t('only-allocations-of-a-single-allocation-variant-can-be-copied');
        return {
            ...entry,
            copyAllocationsButtonDisabledExplanation: explanation,
        };
    });
}

function abortCopyingAllocations(id: string) {
    enrichedEntries.value = enrichedEntries.value.map((entry) => {
        if (entry.type === 'Existing-Allocation-Variant') {
            if (entry.id === id) {
                return {
                    ...entry,
                    copyAllocationsStatus: 'Hidden',
                    copyAllocationsButtonDisabledExplanation: getCopyDisabledExplanation(),
                };
            }

            return {
                ...entry,
                copyAllocationsButtonDisabledExplanation: getCopyDisabledExplanation(),
            };
        }

        return entry;
    });
}

async function createAllocationVariant({
    label,
    selectedSource,
}: {
    label: string;
    selectedSource: { id: string; name: string } | null;
}) {
    const trimmedLabel = label.trim();
    newVariantLabel.value = trimmedLabel;
    createAllocationVariantStatus.value = 'Visible';

    function onDone(isSuccess: boolean) {
        if (isSuccess) {
            createAllocationVariantStatus.value = 'Hidden';
            newVariantLabel.value = '';
        } else {
            createAllocationVariantStatus.value = 'Error';
        }
    }

    createAllocationVariantStatus.value = 'Loading' as const;

    emit('createNewVariant', {
        allocationVariantLabel: trimmedLabel,
        copyAllocationsFrom: selectedSource?.id ?? null,
        onDone,
    });
}

function formatId(index: number, variantName: string): string {
    if (variantName) {
        return `BV${String(index).padStart(3, '0')} (${variantName})`;
    }
    return `BV${String(index).padStart(3, '0')}`;
}

watch([activeIdRef, entriesRef], ([newId, newEntries]) => {
    const activeExistingAllocationVariantEntry = newEntries
        .filter((entry): entry is ExistingAllocationVariantEntry => entry.type === 'Existing-Allocation-Variant')
        .find((entry) => entry.id === newId);

    if (activeExistingAllocationVariantEntry && !activeExistingAllocationVariantEntry.isVisible) {
        changeVisibility(newId, true);
    }
});

function changeHighlight(activeId: string | undefined) {
    emit('changeHighlight', activeId);
}

function changeVisibility(allocationVariantId: string, isVisible: boolean) {
    emit('changeVisibility', {
        allocationVariantId,
        isVisible,
    });
}
</script>
